/* eslint-disable @atlassian/relay/unused-fields */
import React, { useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { JiraNotificationPreferenceInput } from '@atlassian/jira-relay/src/__generated__/globalNotificationsConfigHead_HeadCheckbox_updateGlobalPreferencesMutation.graphql';
import type {
	JiraNotificationType,
	JiraNotificationChannelType,
	notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_allPreferences$key,
} from '@atlassian/jira-relay/src/__generated__/notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_allPreferences.graphql';
import type { notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_enabledDataFragment$key } from '@atlassian/jira-relay/src/__generated__/notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_enabledDataFragment.graphql';
import { EMAIL, IN_PRODUCT } from '../../constants.tsx';
import type {
	JiraNotificationCategoryType,
	JiraNotificationGraphQLType,
	JiraNotificationPreference,
	JiraNotificationPreferenceWithCategory,
} from '../../types.tsx';
import { NotificationsConfigCheckbox } from '../notifications-config-checkbox/index.tsx';

const notificationTypes: JiraNotificationGraphQLType[] = [
	'issueAssigned',
	'issueUpdated',
	'issueDeleted',
	'commentCreated',
	'commentEdited',
	'commentDeleted',
	'issueCreated',
	'issueMoved',
	'mentionsCombined',
	'worklogCombined',
	'miscellaneousIssueEventCombined',
];

// TODO: Move these functions to utils
const preferenceCategoryMap: Partial<Record<JiraNotificationType, JiraNotificationCategoryType>> = {
	ISSUE_ASSIGNED: 'ISSUE_ASSIGNED',
	ISSUE_UPDATED: 'ISSUE_CHANGES',
	ISSUE_DELETED: 'ISSUE_CHANGES',
	COMMENT_CREATED: 'COMMENT_CHANGES',
	COMMENT_EDITED: 'COMMENT_CHANGES',
	COMMENT_DELETED: 'COMMENT_CHANGES',
	ISSUE_CREATED: 'ISSUE_CHANGES',
	ISSUE_MOVED: 'ISSUE_CHANGES',
	MENTIONS_COMBINED: 'ISSUE_MENTIONED',
	WORKLOG_COMBINED: 'ISSUE_WORKLOG_CHANGES',
	MISCELLANEOUS_ISSUE_EVENT_COMBINED: 'ISSUE_MISCELLANEOUS',
};

const createJiraNotificationPreferenceNew = (
	notificationPreference: JiraNotificationPreference,
	isEnabled: boolean,
	channelClicked: JiraNotificationChannelType,
) => {
	const jiraNotificationPreference: JiraNotificationPreferenceWithCategory = {
		type: notificationPreference.type,
		id: notificationPreference.id,
		emailChannel: {
			channel: notificationPreference.emailChannel.channel,
			isEnabled:
				channelClicked === EMAIL ? isEnabled : notificationPreference.emailChannel.isEnabled,
			isEditable: notificationPreference.emailChannel.isEditable,
		},
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		category: preferenceCategoryMap[
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			notificationPreference.type as JiraNotificationType
		] as JiraNotificationCategoryType,
	};

	const { inProductChannel } = notificationPreference;

	if (inProductChannel) {
		jiraNotificationPreference.inProductChannel = {
			channel: inProductChannel.channel,
			isEnabled: channelClicked === IN_PRODUCT ? isEnabled : inProductChannel.isEnabled,
			isEditable: inProductChannel.isEditable,
		};
	}
	return jiraNotificationPreference;
};

const belongsToCategoryGroup = (
	jiraNotificationType: JiraNotificationType,
	categoryGroup: JiraNotificationCategoryType[],
) => {
	const category = preferenceCategoryMap[jiraNotificationType];
	return category && categoryGroup.includes(category);
};

const isChannelEditable = (
	notificationPreference: JiraNotificationPreference,
	channel: JiraNotificationChannelType,
) => {
	if (channel === EMAIL) {
		return notificationPreference.emailChannel?.isEditable;
	}
	if (channel === IN_PRODUCT) {
		return notificationPreference.inProductChannel?.isEditable;
	}
	return false;
};

export const HeadCheckbox = ({
	label,
	channel,
	allPreferences,
	enabledDataFragment,
	neverUnselected = false,
	commitMutation,
	id,
	categories,
}: {
	id: string;
	label: string;
	channel: JiraNotificationChannelType;
	allPreferences: notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_allPreferences$key;
	// if true, the checkbox will never be unselected because there are disabled, always on channels
	neverUnselected?: boolean;
	enabledDataFragment: notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_enabledDataFragment$key;
	commitMutation: (
		newPreferencesAsList: readonly JiraNotificationPreferenceInput[],
		newPreferences: {
			[key: string]: JiraNotificationPreference;
		},
	) => void;
	categories?: JiraNotificationCategoryType[];
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const enabledData = useFragment(
		graphql`
			fragment notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_enabledDataFragment on JiraNotificationGlobalPreference {
				options @required(action: THROW) {
					isEmailNotificationEnabled
				}
			}
		`,
		enabledDataFragment,
	);
	const data = useFragment(
		graphql`
			fragment notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_allPreferences on JiraNotificationPreferences {
				issueAssigned @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				issueUpdated @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				issueDeleted @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				commentCreated @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				commentEdited @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				commentDeleted @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				issueCreated @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				issueMoved @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				mentionsCombined @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				worklogCombined @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
				miscellaneousIssueEventCombined @required(action: THROW) {
					id @required(action: THROW)
					type @required(action: THROW)
					emailChannel @required(action: THROW) {
						channel
						isEnabled
						isEditable
					}
					inProductChannel {
						channel
						isEnabled
						isEditable
					}
				}
			}
		`,
		allPreferences,
	);

	const optionsInNewDesign = useMemo(() => {
		if (channel === EMAIL) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return Object.values(data).filter((pref) => {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const preference = pref as JiraNotificationPreference;
				return (
					preference &&
					preference.type &&
					preference.emailChannel.isEditable &&
					categories &&
					belongsToCategoryGroup(preference.type, categories)
				);
			}) as unknown as JiraNotificationPreference[];
		}
		if (channel === IN_PRODUCT) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return Object.values(data).filter((pref) => {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const preference = pref as JiraNotificationPreference;
				return (
					preference &&
					preference.type &&
					preference.inProductChannel?.isEditable &&
					categories &&
					belongsToCategoryGroup(preference.type, categories)
				);
			}) as unknown as JiraNotificationPreference[];
		}

		return [];
	}, [categories, channel, data]);

	const numberOfSelectedOptions: number = useMemo(() => {
		if (channel === EMAIL) {
			return optionsInNewDesign.filter((option) => option?.emailChannel.isEnabled).length;
		}
		if (channel === IN_PRODUCT) {
			return optionsInNewDesign.filter((option) => option?.inProductChannel?.isEnabled).length;
		}
		return 0;
	}, [channel, optionsInNewDesign]);

	const createElementOfNewPreferencesAsList = useCallback(
		(type: string | null | undefined, isEnabled: boolean) => ({
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			type: type as JiraNotificationType,
			channel,
			isEnabled,
		}),
		[channel],
	);

	const isChecked = numberOfSelectedOptions > 0 || neverUnselected;
	const isIndeterminate = isChecked && numberOfSelectedOptions < optionsInNewDesign.length;

	const onChange = useCallback(
		(_event: React.ChangeEvent<HTMLInputElement>) => {
			// For a channel, if no notification types are selected or indeterminate, enable the channel.
			// Else disable the channel.
			const newChannelEnabledValue = numberOfSelectedOptions < optionsInNewDesign.length;
			const newPreferencesWithChannels = notificationTypes.reduce(
				(acc: { [key: string]: JiraNotificationPreference }, val) => {
					const notificationPreference = data[val];
					if (
						notificationPreference &&
						categories?.length &&
						belongsToCategoryGroup(notificationPreference.type, categories) &&
						isChannelEditable(notificationPreference, channel)
					) {
						acc[val] = createJiraNotificationPreferenceNew(
							notificationPreference,
							newChannelEnabledValue,
							channel,
						);
					}

					return acc;
				},
				{},
			);
			if (data) {
				const newPreferencesAsList: JiraNotificationPreferenceInput[] = Object.values(
					newPreferencesWithChannels,
				).reduce((acc: JiraNotificationPreferenceInput[], val: JiraNotificationPreference) => {
					const type = val.type?.toUpperCase().replace(/ /g, '_');
					acc.push(createElementOfNewPreferencesAsList(type, newChannelEnabledValue));
					return acc;
				}, []);
				commitMutation(newPreferencesAsList, newPreferencesWithChannels);

				let previousValue = 'unchecked';
				let currentValue = 'unchecked';
				if (isIndeterminate) {
					previousValue = 'mixed';
				} else if (isChecked) {
					previousValue = 'checked';
				}
				if (numberOfSelectedOptions < optionsInNewDesign.length) {
					currentValue = 'checked';
				} else if (numberOfSelectedOptions === optionsInNewDesign.length) {
					currentValue = neverUnselected ? 'mixed' : 'unchecked';
				}

				fireTrackAnalytics(createAnalyticsEvent({}), 'checkbox updated', 'notificationCheckbox', {
					type: 'all',
					...Object.keys(newPreferencesWithChannels).reduce<Record<string, string>>((acc, key) => {
						let oldPreference;
						if (channel === EMAIL) {
							oldPreference =
								/* eslint-disable @typescript-eslint/consistent-type-assertions */
								(data[key as keyof typeof data] as JiraNotificationPreference).emailChannel
									.isEnabled;
							/* eslint-enable @typescript-eslint/consistent-type-assertions */
						} else if (channel === IN_PRODUCT) {
							oldPreference = /* eslint-disable @typescript-eslint/consistent-type-assertions */
							(data[key as keyof typeof data] as JiraNotificationPreference).inProductChannel
								?.isEnabled;
							/* eslint-enable @typescript-eslint/consistent-type-assertions */
						}
						const newPreference = newChannelEnabledValue;

						if (oldPreference === newPreference) {
							acc[key] = 'unchanged';
						} else if (newPreference) {
							acc[key] = 'checked';
						} else {
							acc[key] = 'unchecked';
						}
						return acc;
					}, {}),
					channel,
				});
				fireUIAnalytics(createAnalyticsEvent({}), 'checkbox clicked', 'all', {
					channel,
					previousValue,
					currentValue,
				});
			}
		},
		[
			numberOfSelectedOptions,
			optionsInNewDesign,
			data,
			categories,
			channel,
			commitMutation,
			createAnalyticsEvent,
			createElementOfNewPreferencesAsList,
			isIndeterminate,
			isChecked,
			neverUnselected,
		],
	);

	return (
		<NotificationsConfigCheckbox
			id={id}
			name={id}
			testId={`personal-settings-notifications-config-table.ui.notifications-config-head.${id}-head-checkbox`}
			label={label}
			isChecked={isChecked}
			isIndeterminate={isIndeterminate}
			isDisabled={enabledData.options.isEmailNotificationEnabled === false && channel === EMAIL}
			onChange={onChange}
		/>
	);
};
